// frontend/src/pages/NewsList.jsx
// Role: Component that displays news list by date/latest

import React, { useState, useEffect } from 'react';
import { fetchLatestNews, fetchNewsByDate } from '../api/news';
import SubscriptionForm from '../components/SubscriptionForm';
import UnsubscribeForm from '../components/UnsubscribeForm';
import { subscriptionAPI } from '../api/subscription';
import './NewsList.css';

function NewsList() {
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);

  const loadLatestNews = async () => {
    try {
      setLoading(true);
      const data = await fetchLatestNews();
      setNewsData(data);
      setCurrentDate(data.date);
      setError(null);
    } catch (err) {
      setError('Failed to fetch latest news');
      console.error('Error loading news:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadNewsByDate = async (date) => {
    try {
      setLoading(true);
      const data = await fetchNewsByDate(date);
      setNewsData(data);
      setCurrentDate(date);
      setError(null);
    } catch (err) {
      setError('Failed to fetch news for the selected date');
      console.error('Error loading news by date:', err);
    } finally {
      setLoading(false);
    }
  };

  const getPreviousDay = (dateStr) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() - 1);
    return date.toISOString().split('T')[0];
  };

  const getNextDay = (dateStr) => {
    const date = new Date(dateStr);
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0];
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  useEffect(() => {
    loadLatestNews();
  }, []);

  const handleSubscribe = async (email) => {
    return await subscriptionAPI.subscribe(email);
  };

  const handleUnsubscribe = async (email) => {
    return await subscriptionAPI.unsubscribe(email);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading latest news...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Error</h2>
        <p>{error}</p>
        <button onClick={loadLatestNews} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="news-list-container">
      <div className="news-header">
        <h1>Stock Market Daily News</h1>
        {newsData && currentDate && (
          <div className="news-date-section">
            <div className="date-navigation">
              <button
                onClick={() => loadNewsByDate(getPreviousDay(currentDate))}
                className="date-nav-button"
              >
                ← Previous Day
              </button>
              <div className="current-date">
                <span>Date: {currentDate}</span>
              </div>
              <button
                onClick={() => loadNewsByDate(getNextDay(currentDate))}
                className="date-nav-button"
                disabled={currentDate >= getTodayDate()}
              >
                Next Day →
              </button>
            </div>
            <button
              onClick={loadLatestNews}
              className="latest-news-button"
            >
              Latest News
            </button>
          </div>
        )}
      </div>

      {newsData && newsData.news_data && newsData.news_data.length > 0 ? (
        <div className="news-grid">
          {newsData.news_data.map((item, index) => (
            <div
              key={index}
              className="news-card"
            >
              <div className="news-card-header">
                <h3 className="news-title">{item.news_title}</h3>
                <span className="news-time">
                  {formatDateTime(item.released_date_time)}
                </span>
              </div>

              <p className="news-summary">{item.summary}</p>

              {item.related_stock_tickers && item.related_stock_tickers.length > 0 && (
                <div className="stock-tickers">
                  <span className="tickers-label">Related Stocks:</span>
                  {item.related_stock_tickers.map((ticker, tickerIndex) => (
                    <a
                      key={tickerIndex}
                      href={`https://stockanalysis.addfly.me/analysis/${ticker}`}
                      className="ticker"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {ticker}
                    </a>
                  ))}
                </div>
              )}

              <div className="news-card-footer">
                <a
                  href={item.news_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="read-more-link"
                >
                  Read Full Article →
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="no-news">
          <p>No news available for this date.</p>
        </div>
      )}

      {/* Subscription Forms - Bottom of page */}
      <div className="forms-section">
        <div className="forms-container">
          <div className="form-wrapper">
            <SubscriptionForm onSubscribe={handleSubscribe} />
          </div>
          <div className="form-wrapper">
            <UnsubscribeForm onUnsubscribe={handleUnsubscribe} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default NewsList;